import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Map } from 'immutable';
// import { observable } from 'mobx';

import classNames from 'classnames';
import moment from 'moment';

import deburr from '../../utils/deburr';
import dataTestValue from '../../utils/testUtils';
import Icon from '../Icon/Icon';
import { AppStateComponent } from '../../AppState/AppState';
import { getTranslationFor } from 'src/locale/lang';
import { observable, makeObservable } from 'mobx';

const odds = require('odds-converter');

const EMAIL_REGEXP = /^([\w\-+]+(?:\.[\w\-+]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/;

class Form extends AppStateComponent {
    static propTypes = {
        className: PropTypes.string,
        disabled: PropTypes.bool,
        onSubmit: PropTypes.func,
        onError: PropTypes.func,
        onChange: PropTypes.func,
        onBlur: PropTypes.func,
        markValid: PropTypes.bool,
        success: PropTypes.string,
        errors: ImmutablePropTypes.map,
    };

    static defaultProps = {
        markValid: true,
        errors: Map(),
    };

    @observable personalIdValidate = null;

    constructor(props, context) {
        super(props, context);

        this.state = {
            disabled: props.disabled,
            data: props.data || {},
            errors: this.parseErrors(props.errors) || {},
            success: props.success,
        };

        this.focused = {};
        this.oldSuccess = props.success;
        this.fieldRefs = {};
        makeObservable(this);
    }

    UNSAFE_componentWillReceiveProps(props) {
        const state = {};

        if (state.disabled !== props.disabled) {
            state.disabled = props.disabled;
        }

        if (props.errors !== this.props.errors) {
            state.errors = this.parseErrors(props.errors) || {};
        }

        if (this.oldSuccess !== props.success) {
            this.oldSuccess = props.success;
            state.success = props.success;
        }

        this.setState(state);
    }

    getBasicError(error) {
        return typeof error !== 'string' ? undefined : (
            <div className='element'>
                <div className='error-message'>
                    <div className='error-message-content'>{error === '' ? 'Unknown error happened' : error}</div>
                </div>
            </div>
        );
    }

    parseErrors(errors) {
        const result = {};

        if (!errors) {
            return result;
        }

        for (const [key, error] of errors.entries()) {
            if (this.fieldRefs[`field:${ key}`]) {
                result[key] = error;
            }
        }

        return result;
    }

    static empty(value) {
        return value === '' || value === null || value === undefined;
    }

    static fieldsValidation = {};

    static fieldsDependentUpon = {};

    static rules = {
        required: function (value) {
            return !Form.empty(value) && value !== false && value != null && String(value).trim().length !== 0
                ? null
                : getTranslationFor('ERROR_REQUIRED_FIELD');
        },

        checked: function (value) {
            return value ? null : getTranslationFor('ERROR_CHECKED_FIELD');
        },

        email: function (value) {
            return Form.empty(value) || EMAIL_REGEXP.test(value) ? null : getTranslationFor('ERROR_INCORRECT_EMAIL');
        },

        password: function (value) {
            if (Form.empty(value)) {
                return true;
            }

            value = deburr(value);

            return /^[0-9a-zA-Z!"#$%&'()*+,\-./:;<=>?@\[\\\]^_`\{|}~]{8,20}$/.test(value)
                ? null
                : getTranslationFor('ERROR_PASSWORD');
        },

        'password-carousel': function (value) {
            if (Form.empty(value)) {
                return true;
            }

            value = deburr(value);

            if (this.props.user) {
                if (this.props.user.get('email') === value) {
                    return getTranslationFor('ERROR_PASSWORD');
                }
            }
            if (value === this.state.data.email) {
                return getTranslationFor('ERROR_PASSWORD');
            }

            return /^(?=.{8,20})(((?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z]))|(((?=[^0-9]*[0-9])|(?=[^@#$%^&+=]*[@#$%^&+=]))(?=[^a-zA-Z]*[a-zA-Z]))).*/g.test(
                value
            )
                ? null
                : getTranslationFor('ERROR_PASSWORD');
        },

        phone: function (value) {
            return Form.empty(value) || /^[0-9 ]+$/.test(value) ? null : getTranslationFor('ERROR_PHONE');
        },

        numeric: function (value) {
            return Form.empty(value) || /^[0-9]+$/.test(value) ? null : getTranslationFor('ERROR_NUMERIC');
        },

        fractional: function (value) {
            const val = value.includes('/');

            return Form.empty(value) || (val && /^[1-9]?[0-9]+(\/[0-9]+)?$/.test(`${ value}`))
                ? null
                : getTranslationFor('ERROR_FRACTIONAL');
        },

        decimal: function (value) {
            const val = parseFloat(value);

            return Form.empty(value) || (!isNaN(val) && val >= 0 && /^[1-9]?[0-9]+(\.[0-9]+)?$/.test(`${ value}`))
                ? null
                : getTranslationFor('ERROR_DECIMAL');
        },

        alpha: function (value) {
            if (Form.empty(value)) {
                return true;
            }

            value = deburr(value);

            return /^[a-zA-Z]+$/.test(value) ? null : getTranslationFor('ERROR_ALPHA_ONLY');
        },

        city: function (value) {
            if (Form.empty(value)) {
                return true;
            }

            value = deburr(value);

            return /^[a-zA-Z0-9 \-'&]+$/.test(value) ? null : getTranslationFor('ERROR_INVALID_CITY');
        },

        address: function (value) {
            if (Form.empty(value)) {
                return true;
            }

            value = deburr(value);

            return /^[a-zA-Z0-9 !"#$&'()*+,\-./:;<=>?@\[\\\]^_`\{|}~]+$/.test(value)
                ? null
                : getTranslationFor('ERROR_INVALID_ADDRESS');
        },

        surname: function (value) {
            if (Form.empty(value)) {
                return true;
            }

            value = deburr(value);

            return /^[a-zA-Z]+['\-a-zA-Z\s]*$/.test(value) ? null : getTranslationFor('ERROR_INVALID_SURNAME');
        },

        alphaSpaces: function (value) {
            if (Form.empty(value)) {
                return true;
            }

            value = deburr(value);

            return /^[a-zA-Z]+['\-a-zA-Z\s]*$/.test(value) ? null : getTranslationFor('ERROR_ALPHA_SPACES_ONLY');
        },

        alphaSpacesNumbers: function (value) {
            if (Form.empty(value)) {
                return true;
            }

            value = deburr(value);

            return /^[a-zA-Z0-9 ]+$/.test(value) ? null : getTranslationFor('ERROR_ALPHA_SPACES_NUMBERS_ONLY');
        },

        cardNumber: function (value) {
            return Form.empty(value) || /^[0-9]{16}$/.test(value.replace(/[ \t\n]/g, ''))
                ? null
                : getTranslationFor('ERROR_CARD_NUMBER');
        },

        cvc: function (value) {
            return Form.empty(value) || /^[0-9]{3}$/.test(value) ? null : getTranslationFor('ERROR_CVC');
        },

        postcode: function (value) {
            return Form.empty(value) || /^[a-zA-Z0-9 \-]{1,10}$/.test(value)
                ? null
                : getTranslationFor('ERROR_INVALID_POSTCODE');
        },

        maxPrecision: function (value, data, length) {
            return Form.empty(value) || new RegExp(`^[0-9]+(.[0-9]{1,${ length }})?$`).test(value)
                ? null
                : getTranslationFor('ERROR_INVALID_PRECISION').replace('%d', length);
        },

        maxLength: function (value, data, length) {
            return Form.empty(value) || value.length <= length
                ? null
                : getTranslationFor('ERROR_MAX_LENGTH').replace('%d', length);
        },

        minLength: function (value, data, length) {
            return Form.empty(value) || value.length >= length
                ? null
                : getTranslationFor('ERROR_MIN_LENGTH').replace('%d', length);
        },

        date: function (date) {
            if (Form.empty(date)) {
                return true;
            }

            return moment(`${ date}`, 'YYYY-MM-DD').isValid() ? null : getTranslationFor('ERROR_INVALID_DATE');
        },

        adult: function (date) {
            if (Form.empty(date)) {
                return true;
            }

            date = `${ date}`;

            return moment(date, 'YYYY-MM-DD').isValid() && moment().diff(date, 'years') >= 18
                ? null
                : getTranslationFor('ERROR_ADULT');
        },

        halfDecimal: function (value) {
            if (Form.empty(value)) {
                return true;
            }

            return Math.abs(value % 1) === 0.5 ? null : getTranslationFor('ERROR_HALF_DECIMAL');
        },

        positive: function (value) {
            if (Form.empty(value)) {
                return true;
            }

            return value >= 0 ? null : getTranslationFor('ERROR_POSITIVE');
        },

        canBeEmpty: function (value) {
            return null;
        },

        range: function (value, data, { start, end }) {
            value = parseFloat(value);
            return isNaN(value) || (value >= start && value <= end)
                ? null
                : getTranslationFor('ERROR_INVALID_RANGE').replace('{1}', start).replace('{2}', end);
        },

        fractionalRange: function (value, data, { start: startInit, end: endInit }) {
            const valueArr = String(value).split('/');
            const startArr = String(startInit).split('/');
            const endArr = String(endInit).split('/');
            value = odds.fraction.toDecimal(valueArr[0], valueArr[1]);
            const start = odds.fraction.toDecimal(startArr[0], startArr[1]);
            const end = odds.fraction.toDecimal(endArr[0], endArr[1]);
            return isNaN(value) || (value >= start && value <= end)
                ? null
                : getTranslationFor('ERROR_INVALID_RANGE').replace('{1}', startInit).replace('{2}', endInit);
        },
    };

    getFieldParameters(name) {
        const uncontrolledInput = ['email', 'password'];

        let value =
            (this.state.changeTmp && this.state.changeTmp[name]) ||
            (this.state.data[name] && this.state.data[name].hasOwnProperty('value')
                ? this.state.data[name].value
                : this.state.data[name]);

        if (!value && uncontrolledInput.includes(name)) {
            value = '';
        }

        const params = {
            name,
            onFocus: (e) => this.onFocus(name, e),
            onBlur: (e) => this.onBlur(name, e),
            onChange: (e) => this.onChange(name, e),
            value,
        };

        const readOnly =
            this.state.data[name] && this.state.data[name].hasOwnProperty('readOnly')
                ? this.state.data[name].readOnly
                : false;

        if (this.state.disabled || readOnly) {
            params.disabled = true;
        }

        return params;
    }

    getRadioParameters(name, value) {
        return {
            name,
            onFocus: (e) => this.onFocus(name, e),
            onBlur: (e) => this.onBlur(name, e),
            onChange: (e) => this.onChange(name, e, false, value),
            value: this.state.data[name],
        };
    }

    hasErrors(errors) {
        return Object.keys(errors ? this.parseErrors(errors) : this.state.errors || {}).length;
    }

    clearError(name) {
        const state = {
            invalid: this.state.invalid || {},
            errors: this.state.errors || {},
        };

        delete state.invalid[name];
        delete state.errors[name];

        this.setState(state);
    }

    test(rule, value) {
        let options;

        if (typeof rule === 'object' && rule.length) {
            [rule, options] = rule;
        }

        if (typeof rule === 'string' && Form.rules[rule]) {
            rule = Form.rules[rule];
        }

        return rule.call(this, value, this.state.data, options);
    }

    getFieldsValidation() {
        return this.constructor.fieldsValidation || {};
    }

    getFieldsDependencies() {
        return this.constructor.fieldsValidation || {};
    }

    validate(name, calledFromDependency) {
        let validation = this.getFieldsValidation()[name],
            value =
                (this.state.changeTmp && this.state.changeTmp[name]) ||
                (this.state.data[name] && this.state.data[name].hasOwnProperty('value')
                    ? this.state.data[name].value
                    : this.state.data[name]),
            message = null;

        if (validation) {
            for (let i = 0; i < validation.length; i++) {
                const err = this.test(validation[i], value);

                // Ignore 'required' rule for dependent fields; can be overriden by [ 'required' ] rule
                if (calledFromDependency && validation[i] === 'required') {
                    continue;
                }

                if (typeof err === 'string') {
                    message = err;
                    break;
                }
            }

            const state = {
                invalid: this.state.invalid || {},
                errors: this.state.errors || {},
                success: undefined,
            };

            state.invalid[name] = typeof message === 'string';
            state.errors[name] = message;

            this.state.invalid = state.invalid;
            this.state.errors = state.errors;

            this.setState(state);
        }

        const dependentUpon = this.getFieldsDependencies()[name];

        if (dependentUpon) {
            dependentUpon.forEach((field) => {
                if (calledFromDependency !== field) {
                    this.validate(field, name);
                }
            });
        }

        return message;
    }

    isValid() {
        let valid = true,
            rules = this.getFieldsValidation();

        Object.keys(rules).forEach((name) => {
            if (this.validate(name)) {
                valid = false;
            }
        });

        return valid;
    }

    onFocus(name) {
        this.clearError(name);
        this.focused[name] = this.state.data[name];
    }

    onBlur(name) {
        const { onBlur } = this.props;

        const error = this.validate(name);

        if (onBlur) {
            onBlur(name, this.state.data[name], this.focused[name], error);
        }

        delete this.focused[name];
    }

    onChange(field, e, checkbox, value) {
        let val;

        if (checkbox) {
            val = e.target.checked;
        } else if (value) {
            val = value;
        } else {
            const el = (e && e.target) || e;

            if (el && el.multiple) {
                val = [].filter.call(el.options, (o) => o.selected).map((o) => o.value);
            } else {
                val = el && (el.value || el.value === '') ? el.value : el;
            }
        }
        if (this.state.data[field] && this.state.data[field].hasOwnProperty('value')) {
            this.state.data[field].value = val;
        } else {
            this.state.data[field] = val;
        }

        this.setState({
            data: this.state.data,
        });

        this._onChange(field, this.state.data[field], this.state.data);
    }

    _onChange(field, value, data) {
        if (this.props.onChange) {
            this.props.onChange(field, value, data);
        }
    }

    getCheckbox(name, label, key, disabled, visible) {
        const errors = this.state.errors[name],
            invalid = this.state.invalid && this.state.invalid[name];

        const visibility = visible ? visible : {};

        const props = {
            onChange: (e) => {
                this.onChange(name, e, true);
                this.validate(name);
            },
        };

        let value = this.state.data[name]
            ? this.state.data[name].hasOwnProperty('value')
                ? this.state.data[name].value
                : this.state.data[name]
            : false;

        if (name.startsWith('spOnly-')) {
            const newValue = this.state.data[name];
            const sport = name.split('-')[1] ?? null;
            const spOnlySports = this.state.data.spOnlySports ?? [];

            value = newValue ?? spOnlySports.includes(sport) ?? false;
        }

        if (value === null && name === 'cancelWithdrawal') {
            value = true;
        }

        if (value === null && name === 'promoBanned') {
            value = false;
        }

        if (value) {
            props.checked = 'checked';
        }

        const readOnly =
            this.state.data[name] && this.state.data[name].hasOwnProperty('readOnly')
                ? this.state.data[name].readOnly
                : false;

        if (disabled || readOnly) {
            props.disabled = true;
        }

        return (
            <div
                style={visibility}
                key={key}
                ref={(el) => { this.fieldRefs[`field:${ props.name}`] = el; }}
                className={classNames([
                    `element checkbox${ props.disabled ? ' disabled' : ''}`,
                    {
                        error: Boolean(errors),
                        invalid,
                    },
                ])}
                data-test={dataTestValue(label)}
            >
                <label>
                    <input
                        type='checkbox'
                        {...props}
                    />
                    <span dangerouslySetInnerHTML={{ __html: label }} />
                </label>
            </div>
        );
    }

    getField(field, infoString) {
        const props = field.props,
            errors = this.state.errors[props.name],
            invalid = this.state.invalid && this.state.invalid[props.name],
            data = this.state.data,
            val = this.getFieldsValidation(),
            isRequired = val && val[props.name] && val[props.name].indexOf('required') !== -1;

        const visibility = props.visibility ? props.visibility : {};

        if (props.type === 'checkbox') {
            return this.getCheckbox(props.name, props.label);
        } else if (field.type === 'select') {
            if (!data[props.name] && props.children) {
                data[props.name] = props.children[0] && props.children[0].props.value;
            }

            return (
                <div
                    ref={(el) => { this.fieldRefs[`field:${ props.name}`] = el; }}
                    className={classNames([
                        'element',
                        {
                            empty: Form.empty(data[props.name]),
                            error: Boolean(errors),
                            required: isRequired,
                            hasLabel: props.label,
                            invalid,
                            valid:
                                invalid === false && !Form.empty(data[props.name]) && !errors && this.props.markValid,
                        },
                    ])}
                >
                    <div
                        style={visibility}
                        className={classNames(['select-wrapper', { 'select-multiple': props.multiple }])}
                        label={props.label}
                        name={props.name}
                        data-test={dataTestValue(field)}
                    >
                        {field}
                        {props.label ? <label>{props.label}</label> : ''}
                    </div>
                    {errors ? (
                        <div className='error-message'>
                            <div className='error-message-content'>{errors}</div>
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            );
        }

        return (
            <div
                style={visibility}
                ref={(el) => { this.fieldRefs[`field:${ props.name}`] = el; }}
                className={classNames([
                    'element',
                    {
                        empty: Form.empty(data[props.name]),
                        error: Boolean(errors),
                        required: isRequired,
                        hasLabel: props.label,
                        invalid: invalid,
                        valid: invalid === false && !Form.empty(data[props.name]) && !errors && this.props.markValid,
                    },
                ])}
                data-test={dataTestValue(field)}
            >
                {field}
                {props.label ? <label>{props.label}</label> : ''}
                {errors ? (
                    <div className='error-message'>
                        <div className='error-message-content'>{errors}</div>
                    </div>
                ) : (
                    ''
                )}
                {infoString ? (
                    <span
                        className='element-help'
                        title={infoString}
                    >
                        <Icon name='info' />
                    </span>
                ) : undefined}
            </div>
        );
    }

    onSubmit(e) {
        if (this.state.disabled) {
            return;
        }

        const isValid = this.isValid();
        if (isValid && this.props.onSubmit) {
            for (const name in this.state.data) {
                if (this.state.data[name] && this.state.data[name].hasOwnProperty('value')) {
                    this.state.data[name] = this.state.data[name].value;
                } else if (
                    this.state.data[name] &&
                    (this.state.data[name].hasOwnProperty('hidden') || this.state.data[name].hasOwnProperty('readOnly'))
                ) {
                    this.state.data[name] = '';
                }
            }
            this.props.onSubmit(this.state.data, e);
        } else if (!isValid && this.props.onError) {
            this.props.onError(this.state.errors);
        }
    }

    renderForm() {}

    render() {
        const className = classNames([this.props.className, 'form', { disabled: this.state.disabled }]);

        return (
            <form
                method='POST'
                onSubmit={(e) => this.onSubmit(e)}
                className={className}
            >
                {this.renderForm()}
            </form>
        );
    }
}

export default Form;
